import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import isExternalLink from 'bundles/cds-labs/components/Modules/utils/isExternalLink';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

type Props = (typeof TrackedA extends React.ComponentType<infer P> ? P : never) & {
  onClick?: () => void;
};

type PropsToComponent = React.PropsWithChildren<Props> & {
  openInNewTab?: boolean;
};

/**
 * This button wraps a CDS link and adds tracking while preserving UTM parameters from the href
 *
 * Additionally:
 * - It uses Eventing v2 to track the click event.
 * - The new link will preserve the current search params.
 */
function PreservedUtmLink({
  href,
  children,
  openInNewTab,
  onClick,
  ...otherProps
}: React.PropsWithChildren<PropsToComponent>) {
  const isRelative = !href.startsWith('http');
  const isExternal = !isRelative && isExternalLink(href);

  const location = useLocation();

  // preserve utm and coursera params
  const query = new URLSearchParams();
  if (!isExternal) {
    const currentSearch = new URLSearchParams(location.search);
    for (const [key, value] of currentSearch.entries()) {
      if (['preview', 'renderMode', 'locale'].includes(key)) {
        query.set(key, value);
      }
    }
  }

  // merge the new search params to the current search params
  const [hrefWithoutSearch, newSearch] = href.split('?');
  const newQuery = new URLSearchParams(newSearch);
  for (const [key, value] of newQuery.entries()) {
    // note: don't use query.append() since we want to replace the existing value
    query.set(key, value);
  }

  const search = new URLSearchParams(query).toString();
  const shouldOpenInNewTab = openInNewTab || isExternal;

  return (
    <TrackedA
      href={`${hrefWithoutSearch}${search ? `?${search}` : ''}`}
      rel={shouldOpenInNewTab ? 'noreferrer noopener' : undefined}
      target={shouldOpenInNewTab ? '_blank' : undefined}
      onClick={onClick}
      {...otherProps}
    >
      {children}
    </TrackedA>
  );
}

export default PreservedUtmLink;
